export const FALLBACK_EVENT_COLOR = "#738089";

export const GROUP_BASE_COLORS = {
  "549": "#73889B",
  "557": "#F20A46",
  "424": "#F20A46",
  "18": "#E85A5A",
  "19": "#2363F7",
  "587": "#2F7CF6",
  "663": "#3E8CF4",
  "706": "#4B9AE2",
  "597": "#2FA3B8",
  "20": "#8A6BD1",
  "629": "#2D8F83",
  "647": "#D18A3C",
  "431": "#9FB347",
  "730": "#B774A4",
};

const SEMANTIC_LIGHTEN_OFFSET = 0.035;
const SEMANTIC_DARKEN_OFFSET = -0.035;
const MIN_VARIANT_LIGHTNESS = 0.22;
const MAX_VARIANT_LIGHTNESS = 0.78;
const COLOR_VARIANT_TIERS = [
  { h: 0, s: 0, l: 0 },
  { h: 20, s: 0.08, l: 0.06 },
  { h: -20, s: 0.08, l: -0.06 },
  { h: 34, s: 0.03, l: 0.1 },
  { h: -34, s: 0.03, l: -0.1 },
  { h: 48, s: 0.06, l: 0 },
  { h: -48, s: 0.06, l: 0 },
  { h: 12, s: -0.06, l: 0.12 },
];

const toStringKey = (value) => {
  if (value === undefined || value === null) {
    return "";
  }
  return String(value);
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const normalizeHex = (hex) => {
  if (typeof hex !== "string") {
    return FALLBACK_EVENT_COLOR;
  }

  const normalized = hex.startsWith("#") ? hex.slice(1) : hex;

  if (!/^[\da-fA-F]{6}$/.test(normalized)) {
    return FALLBACK_EVENT_COLOR;
  }

  return `#${normalized.toUpperCase()}`;
};

const hashString = (value = "") => {
  const normalized = String(value);
  let hash = 0;

  for (let index = 0; index < normalized.length; index += 1) {
    hash = (hash * 31 + normalized.charCodeAt(index)) >>> 0;
  }

  return hash;
};

const hexToRgb = (hex) => {
  const normalized = normalizeHex(hex).slice(1);
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
};

const rgbToHex = ({ r, g, b }) => {
  return `#${clamp(Math.round(r), 0, 255)
    .toString(16)
    .padStart(2, "0")}${clamp(Math.round(g), 0, 255)
    .toString(16)
    .padStart(2, "0")}${clamp(Math.round(b), 0, 255)
    .toString(16)
    .padStart(2, "0")}`.toUpperCase();
};

const rgbToHsl = ({ r, g, b }) => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;

  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));

    switch (max) {
      case rn:
        h = ((gn - bn) / delta) % 6;
        break;
      case gn:
        h = (bn - rn) / delta + 2;
        break;
      default:
        h = (rn - gn) / delta + 4;
        break;
    }

    h *= 60;
    if (h < 0) {
      h += 360;
    }
  }

  return { h, s, l };
};

const hslToRgb = ({ h, s, l }) => {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));

  let rn = 0;
  let gn = 0;
  let bn = 0;

  if (hp >= 0 && hp < 1) {
    rn = c;
    gn = x;
  } else if (hp >= 1 && hp < 2) {
    rn = x;
    gn = c;
  } else if (hp >= 2 && hp < 3) {
    gn = c;
    bn = x;
  } else if (hp >= 3 && hp < 4) {
    gn = x;
    bn = c;
  } else if (hp >= 4 && hp < 5) {
    rn = x;
    bn = c;
  } else if (hp >= 5 && hp <= 6) {
    rn = c;
    bn = x;
  }

  const m = l - c / 2;

  return {
    r: (rn + m) * 255,
    g: (gn + m) * 255,
    b: (bn + m) * 255,
  };
};

const shiftLightness = (hex, deltaLightness) => {
  const hsl = rgbToHsl(hexToRgb(hex));
  const adjusted = {
    ...hsl,
    l: clamp(hsl.l + deltaLightness, 0, 1),
  };
  return rgbToHex(hslToRgb(adjusted));
};

const applyDeterministicVariant = (hex, parameterKey) => {
  const normalizedColor = normalizeHex(hex);
  const stableKey = toStringKey(parameterKey);

  if (!stableKey) {
    return normalizedColor;
  }

  const hash = hashString(stableKey);
  const hsl = rgbToHsl(hexToRgb(normalizedColor));
  const tier = COLOR_VARIANT_TIERS[hash % COLOR_VARIANT_TIERS.length];

  const adjusted = {
    h: (hsl.h + tier.h + 360) % 360,
    s: clamp(hsl.s + tier.s, 0, 1),
    l: clamp(hsl.l + tier.l, MIN_VARIANT_LIGHTNESS, MAX_VARIANT_LIGHTNESS),
  };

  return rgbToHex(hslToRgb(adjusted));
};

const getActionLabel = (action) => {
  return `${action?.title || action?.name || ""}`.toLowerCase();
};

const isLightenLabel = (label) => label.includes("+");

const isDarkenLabel = (label) => {
  return (
    label.includes("-") ||
    label.includes("allowed") ||
    label.includes("mistake") ||
    label.includes("foul") ||
    label.includes("card") ||
    label.includes("offside")
  );
};

const getRelativeLuminance = (hex) => {
  const { r, g, b } = hexToRgb(hex);

  const toLinear = (channel) => {
    const normalized = channel / 255;
    if (normalized <= 0.03928) {
      return normalized / 12.92;
    }
    return ((normalized + 0.055) / 1.055) ** 2.4;
  };

  const rl = toLinear(r);
  const gl = toLinear(g);
  const bl = toLinear(b);

  return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl;
};

export const getActionEventColor = (action = {}) => {
  const groupLexicId =
    action?.group_lexic_id ?? action?.groupLexicId ?? action?.group_lexic;
  const groupKey = toStringKey(groupLexicId);
  const parameterKey = toStringKey(
    action?.team_parameter_id ??
      action?.teamParameterId ??
      action?.parameter_id ??
      action?.parameterId ??
      action?.id
  );
  const hasKnownGroupColor = Boolean(GROUP_BASE_COLORS[groupKey]);
  const baseColor = GROUP_BASE_COLORS[groupKey] || FALLBACK_EVENT_COLOR;
  const label = getActionLabel(action);
  const variantColor = hasKnownGroupColor
    ? applyDeterministicVariant(baseColor, parameterKey)
    : normalizeHex(baseColor);

  if (isLightenLabel(label)) {
    return shiftLightness(variantColor, SEMANTIC_LIGHTEN_OFFSET);
  }

  if (isDarkenLabel(label)) {
    return shiftLightness(variantColor, SEMANTIC_DARKEN_OFFSET);
  }

  return variantColor;
};

export const buildParameterColorMap = (selectedActions = []) => {
  return selectedActions.reduce((acc, action) => {
    const id = toStringKey(
      action?.team_parameter_id ??
        action?.teamParameterId ??
        action?.parameter_id ??
        action?.parameterId ??
        action?.id
    );

    if (!id) {
      return acc;
    }

    acc[id] = getActionEventColor(action);
    return acc;
  }, {});
};

export const buildMarkerColorMap = (stats = [], parameterColorMap = {}) => {
  return (stats || []).reduce((markerMap, stat) => {
    const parameters = stat?.parameters || [];

    parameters.forEach((parameter) => {
      const parameterId = toStringKey(parameter?.parameter_id ?? parameter?.id);
      const color = parameterColorMap[parameterId] || FALLBACK_EVENT_COLOR;
      const markers = parameter?.markers || [];

      markers.forEach((marker) => {
        const markerId =
          typeof marker === "object" && marker !== null
            ? toStringKey(marker?.id)
            : toStringKey(marker);

        if (!markerId) {
          return;
        }

        markerMap[markerId] = color;
      });
    });

    return markerMap;
  }, {});
};

export const getMarkerEventColor = (eventId, markerColorMap = {}) => {
  const key = toStringKey(eventId);
  if (!key) {
    return FALLBACK_EVENT_COLOR;
  }

  return markerColorMap[key] || FALLBACK_EVENT_COLOR;
};

export const getMarkerRingColor = (fillHex, themeMode = "dark") => {
  const normalizedFill = normalizeHex(fillHex);
  const luminance = getRelativeLuminance(normalizedFill);
  const isLightTheme = themeMode === "light";

  if (luminance > 0.55) {
    return shiftLightness(normalizedFill, isLightTheme ? -0.24 : -0.2);
  }

  return shiftLightness(normalizedFill, isLightTheme ? 0.2 : 0.24);
};

export const getMarkerShadowStyle = (themeMode = "dark") => {
  if (themeMode === "light") {
    return {
      dx: 0.6,
      dy: 0.8,
      fill: "#040802",
      opacity: 0.2,
    };
  }

  return {
    dx: 0.7,
    dy: 0.9,
    fill: "#040802",
    opacity: 0.3,
  };
};
